/**
 * BlowUpBuddies scroll reveal.
 *
 * Fades and lifts content in once, as it enters the viewport. Elements opt in with `[data-bub-reveal]`
 * (an inline `--bub-delay` pins their timing); stock Horizon sections are tagged automatically, with
 * grid items staggered one after another. Only opacity and transform animate, so nothing shifts layout.
 *
 * The `bub-reveal-on` class on <html> (set inline in theme.liquid) is what hides content before reveal;
 * it is never set for reduced-motion users or in the theme editor.
 */
(() => {
  const root = document.documentElement;
  if (!root.classList.contains('bub-reveal-on')) return;
  window.bubReveal = true;

  const STAGGER_MS = 90;
  const MAX_STAGGER_STEPS = 6;
  const ITEM_SELECTOR = '.resource-list__item, .product-grid__item, [data-bub-reveal-item]';
  const HEADER_SELECTOR = '.section-resource-list__header, .section-resource-list__content';
  const SECTION_SELECTOR = 'main .shopify-section, .shopify-section-group-footer-group';

  const observer = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

  /** @param {IntersectionObserverEntry[]} entries */
  function onIntersect(entries) {
    const entering = entries
      .filter((entry) => entry.isIntersecting)
      .map((entry) => entry.target)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

    let step = 0;
    for (const el of entering) {
      observer.unobserve(el);

      // A group (e.g. a swipeable row) reveals all of its children together, staggered,
      // so tiles that start off-screen sideways are already visible when swiped in.
      if (el.hasAttribute('data-bub-reveal-group')) {
        el.querySelectorAll('[data-bub-reveal]').forEach((child, index) => {
          if (child instanceof HTMLElement && !child.style.getPropertyValue('--bub-delay')) {
            child.style.setProperty('--bub-delay', `${Math.min(step + index, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
          }
          child.classList.add('is-revealed');
        });
        continue;
      }

      if (el instanceof HTMLElement && !el.style.getPropertyValue('--bub-delay')) {
        el.style.setProperty('--bub-delay', `${Math.min(step, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
        step += 1;
      }
      el.classList.add('is-revealed');
    }
  }

  /** @param {Element} el */
  function watch(el) {
    if (el.classList.contains('is-revealed') || el.hasAttribute('data-bub-watched')) return;
    // Boxless (`display: contents`) or hidden wrappers can neither fade nor be observed.
    const display = getComputedStyle(el).display;
    if (display === 'contents' || display === 'none') return;
    el.setAttribute('data-bub-reveal', '');
    el.setAttribute('data-bub-watched', '');
    observer.observe(el);
  }

  /** @param {ParentNode} scope */
  function scan(scope) {
    scope.querySelectorAll('[data-bub-reveal-group]').forEach((group) => observer.observe(group));
    scope.querySelectorAll('[data-bub-reveal]').forEach((el) => {
      if (!el.closest('[data-bub-reveal-group]')) watch(el);
    });

    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      // Custom sections choreograph their own reveal.
      if (section.querySelector('.bub-load-in, [data-bub-reveal]:not([data-bub-auto])')) return;

      const items = section.querySelectorAll(ITEM_SELECTOR);
      if (items.length) {
        // Tag only the outermost header wrapper so nested offsets never compound.
        section.querySelectorAll(HEADER_SELECTOR).forEach((header) => {
          if (header.parentElement?.closest(HEADER_SELECTOR) || header.querySelector(ITEM_SELECTOR)) return;
          tagAuto(header);
        });
        items.forEach(tagAuto);
      } else {
        section.querySelectorAll(':scope > .section, :scope > footer, :scope > div > .section').forEach(tagAuto);
      }
    });
  }

  /** @param {Element} el */
  function tagAuto(el) {
    el.setAttribute('data-bub-auto', '');
    watch(el);
  }

  scan(document);
})();
